import React, { useEffect, useRef, useState } from 'react';
import {
    Table,
    Toolbar,
    Field,
    Button,
} from 'react-unity';
import { PortOpeningRequestsDirection } from '../../../models/enums/POR/PortOpeningRequestsDirection';
import CSVGeneratorButton from '../../common/CSVGeneratorButton';
import Paginator from '../../common/tables/Paginator';
import { DNDRColumn, RuleColumnsLayout } from './RuleColumnsLayout';
import { VirtualNetwork } from '../../../models/entities/AzureResources/VirtualNetwork';
import { RuleOperation } from '../../../models/enums/POR/RuleOperation';
import './RulesAttestationTable.css';
import { WorkflowState } from '../../../models/enums/WorkflowState';
import ToggleField from '../../common/form-controls/ToggleField';
import { DoNotDecryptRequestsDirection } from '../../../models/enums/DNDR/DoNotDecryptRequestsDirection';
import DoNotDecryptRule from '../../../models/entities/DoNotDecryptRule';
import DNDROperationBadge from './DNDROperationBadge';
import RuleModalSubToInternet from './dndrRule/RuleModalSubToInternet';
import RuleModalSubToOnPrem from './dndrRule/RuleModalSubToOnPrem';
import { RulesFormProps } from './dndrRule/AbstractRulesForm';
import RuleModalSubToSub from './dndrRule/RuleModalSubToSub';

interface RulesAttestationTableProps {
    subscriptionRequestName?: string;
    subscriptionRequestId?: number;
    rules: DoNotDecryptRule[];
    validVirtualNetworks?: VirtualNetwork[];
    dndrDirection: DoNotDecryptRequestsDirection;
    readonly?: boolean;
    onChange: (rules: DoNotDecryptRule[]) => any;
    fromDetails?: boolean;
    currentWorkflowState?: number;
}

const RulesAttestationTable = (props: RulesAttestationTableProps) => {
    const [columns, setColumns] = useState<DNDRColumn[]>([]);
    const [pageRows, setPageRows] = useState<DoNotDecryptRule[]>([]);
    const [ruleModalVisible, setRuleModalVisible] = useState<boolean>(false);
    const [editingRule, setEditingRule] = useState<DoNotDecryptRule>();
    const prevProps = useRef(props.dndrDirection?.value);
    
    useEffect(() => {
        if (prevProps.current)
            setColumns([]);
    }, [props.dndrDirection?.value]);

    useEffect(() => {
        setColumns(RuleColumnsLayout.get(props.dndrDirection));
    }, []);

    const handlePageChange = (page: DoNotDecryptRule[]) => {
        setPageRows(page);
    };

    const handleRuleAttestation = (rule: DoNotDecryptRule, e: any) => {
        rule.operation = RuleOperation[e.target.value];
        handleRuleChange(rule);
    }

    const handleRuleChange = (rule: DoNotDecryptRule) => {
        let { rules } = props;
        const index = rules.indexOf(rule);
        rules = [...rules];
        rules[index] = rule;
        props.onChange(rules);
    };

    const getOperationName = (rule: DoNotDecryptRule) => {
        if (props.fromDetails && props.currentWorkflowState !== WorkflowState.Review.value && props.currentWorkflowState !== WorkflowState.Draft.value){
            return columns.find(column => column.header === 'Operation').cell(rule)
        }
        const operationColumn = columns.find(column => column.header === 'Operation')
        return operationColumn.cell(rule)
    }

    const directionIs = (direction: PortOpeningRequestsDirection) => {
        return props.dndrDirection === direction;
    }

    const handleOnClose = () => {
        setEditingRule(null);
        setRuleModalVisible(false);
    }

    const getRulesModalProps = (): RulesFormProps => {
        let editingRuleCopy = null;
        if (editingRule != null) {
            editingRuleCopy = {...editingRule};
            editingRuleCopy.name = editingRuleCopy.azureName ?? editingRuleCopy.name;
        }
        return {
            visible: ruleModalVisible,
            onConfirm: handleRuleChange,
            onClose: () => handleOnClose(),
            editingRule: editingRule,
            validVirtualNetworks: props.validVirtualNetworks
        };
    }

    const defaultLimit = 30;

    return (
        <>
            <Field>
                <Field.Label>
                    Ruleset
                </Field.Label>
                <Field.Body>
                    <Table
                        header={
                            <Toolbar>
                                {props.readonly &&
                                    <Toolbar.Item right>
                                        <CSVGeneratorButton
                                            data={props.rules}
                                            columns={columns}
                                        />
                                    </Toolbar.Item>
                                }
                            </Toolbar>
                        }
                        footer={
                            <Paginator
                                data={props.rules}
                                onPageChange={handlePageChange}
                            />
                        }
                    >
                        <Table.Head>
                            <Table.Head.Row>
                                <Table.Head.Cell />
                                {columns?.filter(column => column.header !== "Description").map((column) => 
                                    <Table.Head.Cell key={column.header}>
                                        {column.header}
                                    </Table.Head.Cell>
                                )}
                                {!props.fromDetails && (
                                    <Table.Head.Cell className='table__header-cell'>
                                        {'Actions'}
                                    </Table.Head.Cell>
                                )}
                                <Table.Head.Cell className='table__header-cell'>
                                    {''}
                                </Table.Head.Cell>
                            </Table.Head.Row>
                        </Table.Head>
                        <Table.Body>
                            {pageRows.map((rule, rowIndex) => 
                                <Table.Body.Row key={rowIndex}>
                                    <Table.Body.Cell>
                                        {props.rules.indexOf(rule) + 1}
                                    </Table.Body.Cell>
                                    {columns?.filter(column => column.header !== "Description").map((column, colIndex) => 
                                        <Table.Body.Cell
                                            key={`${rowIndex}-${colIndex}`}
                                            className="em-u-margin-right-auto"
                                            title={(column.cell(rule) || '-')}
                                        >
                                            {column.header === 'Operation' ? (<DNDROperationBadge>{getOperationName(rule)}</DNDROperationBadge>) : (column.cell(rule)?.length > (column.limit || defaultLimit) ?
                                                (`${column.cell(rule).trim().substring(0, (column.limit || defaultLimit))}...`)
                                                :
                                                (column.cell(rule) || '-'))}
                                        </Table.Body.Cell>
                                    )}
                                    {!props.fromDetails && (
                                        <Table.Body.Cell>
                                            <ToggleField
                                                className='em-u-font-size-small-2'
                                                id={`rule-operation-${rowIndex}`}
                                                value={rule.operation.name.toString()}
                                                options={[
                                                    {
                                                        value: RuleOperation.Keep.name,
                                                        text: 'Keep'
                                                    },
                                                    {
                                                        value: RuleOperation.Remove.name,
                                                        text: 'Delete'
                                                    },
                                                ]}
                                                onChange={(e) => handleRuleAttestation(rule, e)}
                                            />
                                        </Table.Body.Cell>
                                    )}
                                    <Table.Body.Cell>
                                        <Button
                                            style={{ zIndex: 0 }}
                                            variant="primary"
                                            size="small"
                                            onClick={() => {
                                                setRuleModalVisible(true)
                                                setEditingRule(rule)
                                            }}
                                        >
                                            See details
                                        </Button>
                                    </Table.Body.Cell>
                                </Table.Body.Row>
                            )}
                        </Table.Body>
                    </Table>
                </Field.Body>
            </Field>
            {directionIs(DoNotDecryptRequestsDirection.SubscriptionToInternet) &&
                <RuleModalSubToInternet
                    {...getRulesModalProps()}
                    subscriptionRequestName={props.subscriptionRequestName}
                    //subscriptionRequestId={props.subscriptionRequestId}
                    isReadOnly={true}
                />}
            {directionIs(DoNotDecryptRequestsDirection.SubscriptionToOnPrem) &&
                <RuleModalSubToOnPrem
                    {...getRulesModalProps()}
                    subscriptionRequestName={props.subscriptionRequestName}
                    //subscriptionRequestId={props.subscriptionRequestId}
                    isReadOnly={true}
                />}
            {directionIs(DoNotDecryptRequestsDirection.SubscriptionToSubscription) &&
                <RuleModalSubToSub
                    {...getRulesModalProps()}
                    subscriptionRequestName={props.subscriptionRequestName}
                    //subscriptionRequestId={props.subscriptionRequestId}
                    isReadOnly={true}
                />}
                
        </>
    );
}

export default RulesAttestationTable;